// React
import React from 'react'
import PropTypes from 'prop-types'

// Libraries
import { clsx } from 'clsx'

// Components
import BCartPicker from 'ui/blocks/Picker/types/cart'
import BPicker from 'ui/blocks/Picker'
import DateTimePicker from 'ui/components/Picker/DateTimePicker'
import Icon from 'ui/components/Icon'
import InputField from 'ui/components/InputField'

// Hooks
import { usePicker } from 'ui/components/Picker/hooks'
import { useFormikContext } from 'formik'

// Shared
import { Field } from 'shared/form'
import { useTranslation } from 'shared/utils/useTranslation'

export const DurationsPicker = ({ children, onOpen, onChange, onBlur, ...props }) => {
  const picker = usePicker()

  const form = useFormikContext()
  const { t } = useTranslation('user')

  const handleDurationChange = (id) => {
    props.onDurationChange(id)
  }

  return (
    <BCartPicker.Durations
      className={clsx({ 'no-times': !picker.useTimes })}
      disableStyling={props.isMobile}
      disabled={props.disabled}
    >
      <BPicker.Field>
        <BPicker.Label>{t('user.store.rental_duration')}</BPicker.Label>
        <InputField
          {...props}
          field={{
            value: props.durationObject?.id,
            onChange: handleDurationChange
          }}
          type="durations"
          innerRef={(ref) => {
            picker.refs.durationsRef.current = ref
          }}
          data-tid="Durations"
          placeholder={t('user.store.rental_duration')}
          usePortal={props.isMobile}
          preventTargetAssign
          hideLabel
        />
      </BPicker.Field>
      <BCartPicker.Period modifiers="width-100p" className={clsx({ 'no-times': !picker.useTimes })} disableStyling>
        <BPicker.Field>
          <BPicker.Label>{t('user.store.start')}</BPicker.Label>
          <Field
            {...props}
            component={DateTimePicker}
            name="starts_at"
            isTimeSelectable={(timestring) => {
              return picker.isTimeSelectable({
                type: 'start',
                timestring,
                durationData: props.durationObject?.data,
                allowPast: picker.allowPast
              })
            }}
            onChange={onChange}
            onBlur={onBlur}
            blockedDays={props.getBlockedDays}
            inputDateRef={picker.refs.fromDateRef}
            inputTimeRef={picker.refs.fromTimeRef}
            mode="from"
            selection={({ hover }) => props.getRange(hover, form.values, props.durationObject)}
            data-tid="Starts at picker"
            onMonthChange={props.onMonthChange?.bind(null, { type: 'start' })}
            onOpen={onOpen?.bind(null, { type: 'start', values: form.values })}
            disabledDate={!props.durationObject}
            disabledTime={!props.durationObject}
            clearCacheRef={(ref) => {
              props.clearCacheRef.current = ref
            }}
            withAvailability
            hideLabel
            initialError
          />
        </BPicker.Field>
        <BPicker.Decorator>
          <Icon icon="arrow-right" />
        </BPicker.Decorator>
        <BPicker.Field>
          <BPicker.Label>{t('user.store.stop')}</BPicker.Label>
          <Field
            {...props}
            component={DateTimePicker}
            onTimeChange={props.onStopTimeChange}
            name="stops_at"
            isTimeSelectable={(timestring) => {
              return picker.isTimeSelectable({ type: 'stop', timestring, allowPast: picker.allowPast })
            }}
            mode="to"
            data-tid="Stops at picker"
            disabledTime={props.isReturnTimeDisabled || !props.durationObject}
            onOpen={onOpen?.bind(null, { type: 'stop', values: form.values })}
            inputDateRef={picker.refs.tillDateRef}
            inputTimeRef={picker.refs.tillTimeRef}
            disabledDate
            withAvailability
            hideLabel
            initialError
          />
        </BPicker.Field>
      </BCartPicker.Period>
      {children}
    </BCartPicker.Durations>
  )
}

DurationsPicker.propTypes = {
  children: PropTypes.node,
  onDurationChange: PropTypes.func,
  durationObject: PropTypes.object,
  getRange: PropTypes.func,
  getBlockedDays: PropTypes.func,
  isReturnTimeDisabled: PropTypes.bool,
  onOpen: PropTypes.func,
  onChange: PropTypes.func,
  onStopTimeChange: PropTypes.func,
  onBlur: PropTypes.func,
  onMonthChange: PropTypes.func,
  onSelectionChange: PropTypes.func,
  isMobile: PropTypes.bool,
  from: PropTypes.instanceOf(Date),
  to: PropTypes.instanceOf(Date),
  disable: PropTypes.shape({
    starts_at: PropTypes.bool,
    stops_at: PropTypes.bool,
    start_location_id: PropTypes.bool,
    stop_location_id: PropTypes.bool
  }),
  disabled: PropTypes.bool,
  clearCacheRef: PropTypes.any
}
